// BackgroundHero.tsx
import React, { ReactNode, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import PageTopSection from '../universal/pageTopSection/PageTopSection';
import { getOverallPadding } from '../../helpers/Themes';

interface BackgroundHeroProps {
    padding?: number;
    image?: string;
    isMobile: boolean;
    fadeOnScroll?: boolean;
    children?: ReactNode;
}

const BackgroundHero: React.FC<BackgroundHeroProps> = ({
    padding,
    image,
    isMobile,
    fadeOnScroll = false,
    children,
}) => {
    const [scrollY, setScrollY] = useState(0);

    // Constants
    const FADE_START = 0;
    const FADE_END = 500;
    const INITIAL_OPACITY = 0.25;
    const MIN_OPACITY = 0.15; // 👈 never fade below this
    const INITIAL_BLUR = 5;
    const MAX_BLUR = 10;

    // Progress helper
    const clamp = (n: number, min = 0, max = 1) =>
        Math.max(min, Math.min(max, n));
    const t = clamp((scrollY - FADE_START) / (FADE_END - FADE_START));

    // Opacity: ease from INITIAL_OPACITY down to MIN_OPACITY (never 0)
    const opacity = fadeOnScroll
        ? INITIAL_OPACITY - t * (INITIAL_OPACITY - MIN_OPACITY)
        : INITIAL_OPACITY;

    // Blur (unchanged, but you can also clamp similarly if you like)
    const blur = fadeOnScroll
        ? INITIAL_BLUR + (MAX_BLUR - INITIAL_BLUR) * t
        : INITIAL_BLUR;

    useEffect(() => {
        if (!fadeOnScroll) return;

        let animationFrame: number;

        const smoothScroll = () => {
            animationFrame = requestAnimationFrame(() => {
                setScrollY((prev) => {
                    const target = window.scrollY;
                    const eased = prev + (target - prev) * 0.5; // 0.05–0.2 = smoother
                    return eased;
                });
                smoothScroll();
            });
        };

        smoothScroll();
        return () => cancelAnimationFrame(animationFrame);
    }, [fadeOnScroll]);

    return (
        <Box
            sx={{
                width: '100vw',
                height: 'auto',
                minHeight: '100vh',
                position: 'relative',
                overflow: 'hidden',
                left: '50%',
                right: '50%',
                marginLeft: '-50vw',
                marginRight: '-50vw',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                color: image ? 'white' : 'inherit',
            }}
        >
            {/* Background image with fade and blur */}
            {image && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: `url(${image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'top center',
                        backgroundRepeat: 'no-repeat',
                        backgroundAttachment: 'fixed',
                        opacity: opacity,
                        transition:
                            'opacity 0.5s ease-out, filter 0.5s ease-out',
                        zIndex: 0,
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background:
                                'linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.6))',
                            zIndex: 0,
                            backdropFilter: `blur(${blur}px)`,
                        },
                    }}
                />
            )}

            {/* Content on top */}
            <Box
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    width: '100%',
                    maxWidth: 1200,
                    margin: image ? '0 auto' : undefined,
                    paddingLeft: image ? 0 : 2,
                    paddingRight: image ? 0 : 2,
                    paddingTop: image ? (isMobile ? 8 : 12) : 0, // push content down slightly
                }}
            >
                <PageTopSection
                    isMobile={isMobile}
                    padding={padding}
                    hideSeperator={true}
                    gapSize={'small'}
                />
                <Box
                    sx={{
                        paddingLeft: getOverallPadding(isMobile, padding),
                        paddingRight: getOverallPadding(isMobile, padding),
                        marginBottom: isMobile ? 10 : 3,
                    }}
                />
                {children} {/* fully opaque */}
            </Box>
        </Box>
    );
};

export default BackgroundHero;
